import { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import type { BrandDetails, MarketingStrategy } from "../types/add-brand-types";
import useBrandApi from "../api/use-add-brand-api";
import { useNavigation } from "./useNavigation";
import { AppRoute } from "../types/routes";
import { fetchBrandList, fetchContentLogs } from "../store/features/dashboard/dashboardSlice";
import type { AppDispatch } from "../store/store";

export interface ToneOption {
  value: string;
  label: string;
}

const DEFAULT_TONE_OPTIONS: ToneOption[] = [
  { value: "professional", label: "Professional" },
  { value: "witty", label: "Witty" },
  { value: "urgent", label: "Urgent" },
  { value: "friendly", label: "Friendly" },
];

function parseCompetitors(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

export function useBrandSettingsModal(
  brandDetails: BrandDetails | null,
  refreshBrandDetails: () => void
) {
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
    isVisible: boolean;
  } | null>(null);
  const navigation = useNavigation();
  const dispatch = useDispatch<AppDispatch>();
  const [brandName, setBrandName] = useState("");
  const [keyCompetitors, setKeyCompetitors] = useState<string[]>([]);
  const [bannedWords, setBannedWords] = useState<string[]>([]);
  const [brandTone, setBrandTone] = useState("");
  const [toneOptions, setToneOptions] =
    useState<ToneOption[]>(DEFAULT_TONE_OPTIONS);
  const { updateBrand, deleteBrand } = useBrandApi();

  useEffect(() => {
    if (isOpen && brandDetails) {
      setBrandName(brandDetails.brand_name ?? "");
      setKeyCompetitors(
        parseCompetitors(brandDetails.marketing_strategy?.competitors)
      );
      setBannedWords(
        Array.isArray(brandDetails.marketing_strategy?.banned_words)
          ? brandDetails.marketing_strategy.banned_words
          : []
      );
      setBrandTone(brandDetails.marketing_strategy?.tone.toLowerCase() ?? "");
    }
  }, [isOpen, brandDetails]);

  const openSettings = useCallback(() => {
    setIsOpen(true);
    setShowDeleteConfirm(false);
  }, []);

  const closeSettings = useCallback(() => {
    setIsOpen(false);
    setShowDeleteConfirm(false);
  }, []);

  const handleSave = useCallback(async () => {
    const updatedBrandDetails: Partial<MarketingStrategy> = {
      ...brandDetails?.marketing_strategy!,
      tone: brandTone,
      competitors: keyCompetitors.join(","),
      banned_words: bannedWords,
    };
    const response = await updateBrand(
      updatedBrandDetails,
      brandDetails?.id ?? ""
    );
    if (response) {
      setToast({
        message: "Settings saved successfully",
        type: "success",
        isVisible: true,
      });
      refreshBrandDetails();
      closeSettings();
    } else {
      setToast({
        message: "Failed to save settings. Please try again.",
        type: "error",
        isVisible: true,
      });
    }
  }, [
    bannedWords,
    brandDetails,
    brandTone,
    closeSettings,
    keyCompetitors,
    refreshBrandDetails,
    updateBrand,
  ]);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      const isDeleted = await deleteBrand(brandDetails?.id ?? "");
      if (isDeleted) {
        navigation.replace(AppRoute.DASHBOARD);
        dispatch(fetchBrandList());
        dispatch(fetchContentLogs());
      }
      setShowDeleteConfirm(false);
      closeSettings();
    } finally {
      setIsDeleting(false);
    }
  }, [brandDetails?.id, closeSettings, deleteBrand, dispatch, navigation]);

  const closeDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const [showCustomToneModal, setShowCustomToneModal] = useState(false);

  const handleAddCustomTone = useCallback(() => {
    setShowCustomToneModal(true);
  }, []);

  const handleCustomToneSubmit = useCallback((customTone: string) => {
    const value = customTone.toLowerCase().replace(/\s+/g, "_");
    const label = customTone.trim();
    setToneOptions((prev) => {
      if (prev.some((o) => o.value === value)) return prev;
      return [...prev, { value, label }];
    });
    setBrandTone(value);
    setShowCustomToneModal(false);
  }, []);

  const closeCustomToneModal = useCallback(() => {
    setShowCustomToneModal(false);
  }, []);

  return {
    isOpen,
    showDeleteConfirm,
    isDeleting,
    toast,
    setToast,
    brandName,
    setBrandName,
    keyCompetitors,
    setKeyCompetitors,
    bannedWords,
    setBannedWords,
    brandTone,
    setBrandTone,
    toneOptions,
    openSettings,
    closeSettings,
    handleSave,
    handleDeleteClick,
    handleConfirmDelete,
    closeDeleteConfirm,
    handleAddCustomTone,
    showCustomToneModal,
    closeCustomToneModal,
    handleCustomToneSubmit,
  };
}
